import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  Signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  mapTo,
  merge,
  switchMap,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
  take,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonPosition } from '@fcom/ui-components/components/buttons/cta-button/enums';
import { ButtonSize, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { ModalButtons } from '@fcom/ui-components/components/modal/interfaces';
import { Step } from '@fcom/common/interfaces/step.interface';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { LocationInfoMap } from '@fcom/dapi/interfaces/itinerary.interface';
import { finShare } from '@fcom/rx/operators/finShare';
import { keepState } from '@fcom/rx/operators/operators';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { Category } from '@fcom/dapi/api/models/category';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { FinnairCart } from '@fcom/dapi/api/models/finnair-cart';
import { FinnairCheckInEligibility } from '@fcom/dapi/api/models/finnair-check-in-eligibility';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { FinnairPassengerItem } from '@fcom/dapi/api/models/finnair-passenger-item';
import { FinnairServiceBoundItem } from '@fcom/dapi/api/models/finnair-service-bound-item';
import { FinnairServiceItem } from '@fcom/dapi/api/models/finnair-service-item';
import { BookingSteps } from '@fcom/common/interfaces/booking/booking-step.interface';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { ScrollService } from '@fcom/common/services/scroll/scroll.service';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { AirOffersStatus } from '@fcom/common/interfaces/booking/air-offers-state.interface';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';

import { nonSeatAncillarySubTotalForJourneySelector } from '../../../../store/selectors/ancillary-sub-total.selector';
import { BookingSummaryService } from '../../../../interfaces/booking-summary.interface';
import {
  ConfirmModal,
  ConfirmModalData,
  ItineraryModal,
  PurchasedServicesForOrder,
} from '../../interfaces/ancillaries-interface';
import { SubviewComponent } from '../../../../components/subview/subview.component';
import { getBoundDetailsModalData } from '../../../../utils/ancillaries.utils';
import { getCheckInEligibilities, getUnpaidSeatsBySegmentId } from '../../../../store/selectors/order.selector';
import { AncillaryLoaderStatus } from '../../enums';
import { ShoppingCartService } from '../../../../services/shopping-cart.service';

@Component({
  selector: 'fin-ancillary-subview',
  styleUrls: ['ancillary-subview.component.scss'],
  templateUrl: 'ancillary-subview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AncillarySubviewComponent<T = void> implements OnInit, OnChanges, OnDestroy {
  elementRef = inject(ElementRef);
  private store$ = inject<Store<BookingAppState>>(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private scrollService = inject(ScrollService);
  private mediaqueryService = inject(MediaQueryService);
  private readonly isMobile = this.mediaqueryService.isBreakpoint('mobile');
  readonly isTabletUp = computed(() => {
    return !this.isMobile();
  });
  private shoppingCartService = inject(ShoppingCartService);

  @ViewChild('scrollContainer', { static: false }) scrollContainer: ElementRef;

  STEPS: BookingSteps = BOOKING_STEPS;

  readonly ButtonPosition = ButtonPosition;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly IconPosition = IconPosition;
  readonly Category = Category;
  readonly ModalButtons = ModalButtons;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  /**
   * A little bit longer than the CSS animation to ensure that the element has slid down
   */
  readonly ANIMATION_DURATION_MS = 350;

  /**
   * Exposes data given in the `open()` call to the template
   */
  readonly data$: Observable<T>;

  readonly AirOffersStatus = AirOffersStatus;
  readonly AncillaryLoaderStatus = AncillaryLoaderStatus;

  @Input()
  subviewName: string;

  @Input()
  boundsOrFlights: Array<FinnairBoundItem | FinnairItineraryItemFlight> = [];

  @Input()
  locations: LocationInfoMap = {};

  @Input()
  titleKey: string;

  @Input()
  backButtonTextKey = 'ancillaries.backToServices';

  @Input()
  category: Category;

  @Input()
  cartOrOrder$: Observable<FinnairCart | FinnairOrder>;

  readonly currentStep = input<Step>();

  @Input()
  unpaidServices$: Observable<BookingSummaryService[]>;

  @Input()
  subtitleMessageKey$: Observable<string>;

  @Input()
  currentIncludedServices!: FinnairServiceItem | null;

  @Input()
  onlyShowAncillaries = false;

  @Input()
  showFareFamilyInAncillarySidebar = true;

  @Input()
  purchasedServices$: Observable<PurchasedServicesForOrder> = of({});

  @Input()
  subviewContext: string;

  @Input()
  maxedOutForFragment$: Observable<Record<string, boolean>>;

  @Input()
  skipEnabled = false;

  @Input()
  skipText = '';

  @Input()
  confirmModalData$ = new BehaviorSubject<ConfirmModal | undefined>({
    open: false,
  });

  @Input()
  hideChangeButton = false;

  @Input()
  ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus> = new BehaviorSubject<AncillaryLoaderStatus>(
    AncillaryLoaderStatus.OFF
  );

  @Input()
  hideCheckoutButton = false;

  @Input()
  isBagsOwnStep = false;

  @Input()
  isShoppingCartMobileView = false;

  @Output()
  closeConfirmModal: EventEmitter<ConfirmModalData> = new EventEmitter<ConfirmModalData>();

  /**
   * Called when the subview is closed after onConfirm, no matter how the subview was closed.
   */

  @Output()
  close: EventEmitter<void> = new EventEmitter();

  @Output()
  changeStep: EventEmitter<Step> = new EventEmitter();

  @Output()
  reassignSeats: EventEmitter<{ currentFlightId: string }> = new EventEmitter();

  @Output()
  secondaryButtonClick: EventEmitter<string> = new EventEmitter();

  @Output()
  flightOrBoundSelected: EventEmitter<FinnairBoundItem | FinnairItineraryItemFlight> = new EventEmitter();

  @ViewChild('closeButton', { static: true })
  closeButton: ElementRef;

  @ViewChild('content', { static: true })
  content: ElementRef;

  @ViewChild('subview', { static: true })
  subview: SubviewComponent;

  @ViewChild('ctaButtonHeightFixRef', { static: false })
  ctaButtonHeightFixRef: ElementRef;

  initialSlideIndex: number;
  slideIndex: number;
  ctaButtonTitle: string;
  passengers$: Observable<FinnairPassengerItem[]>;
  subViewVisible$: Subject<boolean> = new BehaviorSubject(false);
  reassignFreeSeatsVisible$: Subject<boolean> = new BehaviorSubject(false);
  unpaidSeats$: Observable<FinnairServiceBoundItem[]>;
  checkInEligibilities$: Observable<FinnairCheckInEligibility[]>;
  disableButtons$: Observable<boolean>;
  subViewVisibleDelayed$: Observable<boolean>;
  basketOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  subTotalPrice$: Observable<Amount>;
  itineraryModal: ItineraryModal = {
    open: false,
    data: null,
  };
  slideIndexSubject$ = new ReplaySubject<number>(1);
  readonly slideIndex$ = this.slideIndexSubject$.asObservable();
  secondaryButtonDisabled$: Observable<boolean>;
  isNonChargeableAncillary$: Observable<boolean>;
  showShoppingCart: Signal<boolean>;
  readonly showDesktopCart = this.shoppingCartService.showDesktopCart;

  private boundsOrFlightsSubject$: ReplaySubject<Array<FinnairBoundItem | FinnairItineraryItemFlight>> =
    new ReplaySubject<Array<FinnairBoundItem | FinnairItineraryItemFlight>>(1);
  private categorySubject$: ReplaySubject<Category> = new ReplaySubject<Category>(1);
  private customDataSubject$: Subject<T> = new ReplaySubject(1);
  private subscription: Subscription = new Subscription();

  constructor() {
    this.subViewVisibleDelayed$ = this.subViewVisible$.pipe(
      distinctUntilChanged(),
      keepState(this.ANIMATION_DURATION_MS)
    );

    this.disableButtons$ = this.subViewVisible$.pipe(map((visible) => !visible));

    this.data$ = this.customDataSubject$.pipe(finShare());

    this.subTotalPrice$ = this.categorySubject$.pipe(
      switchMap((cat) => this.store$.select(nonSeatAncillarySubTotalForJourneySelector(cat)))
    );
  }

  ngOnInit(): void {
    this.initialSlideIndex = -1;
    this.slideIndexSubject$.next(this.initialSlideIndex);
    this.initializeCtaButtonTitle();

    this.passengers$ = this.cartOrOrder$.pipe(map(({ passengers }) => passengers));

    this.showShoppingCart = this.shoppingCartService.isEnabled;

    this.secondaryButtonDisabled$ = combineLatest([
      this.cartOrOrder$.pipe(map((order) => order?.services?.unpaid?.length === 0)),
      this.subTotalPrice$,
    ]).pipe(map(([noUnpaidItems, subTotal]) => noUnpaidItems && subTotal?.amount === '0' && !subTotal.currencyCode));

    const boundsOrFlights$ = this.boundsOrFlightsSubject$.pipe(
      distinctUntilChanged(
        (prev, next) => prev.map((value) => value.id).join() === next.map((value) => value.id).join()
      )
    );

    this.subscription.add(
      combineLatest([this.slideIndexSubject$, boundsOrFlights$])
        .pipe(
          map(([slideIndex, boundsOrFlights]): [number, FinnairBoundItem | FinnairItineraryItemFlight] => {
            return [slideIndex, boundsOrFlights[slideIndex]];
          })
        )
        .subscribe(([indexToGo, currentBoundOrFlight]) => {
          this.slideIndex = indexToGo;
          this.flightOrBoundSelected.emit(currentBoundOrFlight);
          this.setReassignSeatsEligible(currentBoundOrFlight?.id);
        })
    );

    this.isNonChargeableAncillary$ = this.subTotalPrice$.pipe(
      map((subTotal) => subTotal?.amount === '0' && !!subTotal.currencyCode)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['boundsOrFlights']) {
      this.boundsOrFlightsSubject$.next(changes['boundsOrFlights'].currentValue);
    }
    if (changes['category']) {
      this.categorySubject$.next(changes['category'].currentValue);
    }
  }

  /**
   * Whether or not the subview is visible in DOM taking the animations into account.
   */
  public get visible$(): Observable<boolean> {
    return this.subViewVisible$;
  }

  updateSlideIndex(slideIndex: number, scrollTop: boolean): void {
    this.slideIndexSubject$.next(slideIndex);
    if (this.scrollContainer && scrollTop) {
      this.scrollService.scrollToImmediate(this.scrollContainer);
    }
  }

  /**
   * Open the subview and possibly supply custom data to the instance.
   * @param subviewName name of the subview
   * @param {T} data optional data to expose as `subview.data` in the templates
   * @param {number} slideIndex slide index to be opened
   * @return void
   */
  open(data?: T, slideIndex: number = this.initialSlideIndex): void {
    this.slideIndexSubject$.next(slideIndex);
    this.customDataSubject$.next(data);
    this.subscription = new Subscription();
    this.subViewVisible$.next(true);
    const resize$: Observable<Event> = fromEvent(window, 'resize').pipe(debounceTime(100));
    this.bindCtaButtonHeightFixHandler(resize$);
    this.bindBasketHandling(resize$);
    this.subview.open = true;
  }

  onClose(): void {
    this.closeSubview();
  }

  closeSubview(): void {
    this.slideIndexSubject$.next(this.initialSlideIndex);
    this.subViewVisible$.next(false);
    this.changeDetectorRef.detectChanges();

    this.subview.open = false;
  }

  openBasket(): void {
    this.basketOpen$.next(true);
  }

  openShoppingCart(): void {
    this.shoppingCartService.setVisible(true);
  }

  closeBasket(): void {
    this.basketOpen$.next(false);
  }

  onEdit(step: Step): void {
    this.closeSubview();
    this.changeStep.emit(step);
  }

  viewDetails(): void {
    this.itineraryModal = getBoundDetailsModalData(
      this.cartOrOrder$,
      this.slideIndexSubject$,
      this.boundsOrFlightsSubject$
    );
  }

  handleCtaClick(): void {
    if (this.slideIndex >= this.boundsOrFlights.length - 1) {
      this.onClose();
    } else {
      this.updateSlideIndex(this.slideIndex + 1, true);
    }
  }

  handleSecondaryCtaClick(): void {
    this.subscription.add(
      this.isNonChargeableAncillary$.pipe(take(1)).subscribe((isNonChargeableAncillary) => {
        if (isNonChargeableAncillary) {
          this.handleCtaClick();
        } else {
          this.secondaryButtonClick.emit();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.cleanUp();
    this.elementRef = null;
    this.content = null;
    this.closeButton = null;
  }

  private initializeCtaButtonTitle(): void {
    this.subscription.add(
      combineLatest([this.boundsOrFlightsSubject$, this.slideIndexSubject$]).subscribe(
        ([boundsOrFlights, slideIndex]) => {
          if (this.category === Category.COVER || !boundsOrFlights.length) {
            this.ctaButtonTitle = 'actions.done';
          } else if (slideIndex < boundsOrFlights.length - 1) {
            this.ctaButtonTitle = 'actions.nextFlight';
          } else if (this.isBagsOwnStep) {
            this.ctaButtonTitle = 'ancillaries.bags.actions.addOtherBags';
          } else {
            this.ctaButtonTitle = 'services.productCards.moreTravelExtras';
          }
        }
      )
    );
  }

  private setReassignSeatsEligible(currentBoundOrFlightId: string) {
    this.unpaidSeats$ = this.store$.pipe(getUnpaidSeatsBySegmentId(currentBoundOrFlightId));
    this.checkInEligibilities$ = this.store$.pipe(getCheckInEligibilities());

    this.subscription.add(
      combineLatest([this.unpaidSeats$, this.checkInEligibilities$])
        .pipe(
          map(([unpaidSeats, checkInEligibilities]) => {
            const hasCheckInEligibility = checkInEligibilities.some((eligibility) => {
              const containsBoundId = eligibility.id.includes(currentBoundOrFlightId);
              const hasCheckInWindow = !!eligibility.checkinWindow;
              const hasNoReason = !eligibility.reason;
              return containsBoundId && hasCheckInWindow && hasNoReason;
            });

            const hasUnpaidSeatForSegment = unpaidSeats.some((boundItem) =>
              boundItem.segments.some((segment) => segment.id === currentBoundOrFlightId)
            );

            return hasCheckInEligibility && hasUnpaidSeatForSegment;
          })
        )
        .subscribe((showReassignFreeSeats) => this.reassignFreeSeatsVisible$.next(showReassignFreeSeats))
    );
  }

  private bindCtaButtonHeightFixHandler(resize$: Observable<Event>) {
    const RETRY_TIMES = 1;
    const WAIT_TIME_IN_MS = 500;
    const retry$ = new Subject<true>();
    this.subscription.add(
      merge(
        this.subViewVisibleDelayed$.pipe(filter(Boolean)),
        resize$.pipe(mapTo(true)),
        retry$.pipe(take(RETRY_TIMES))
      )
        .pipe(delay(WAIT_TIME_IN_MS))
        .subscribe(() => {
          if (this.elementRef?.nativeElement) {
            const element = this.elementRef.nativeElement.querySelector('fcom-cta-button .cta-button');
            if (element && this.ctaButtonHeightFixRef && this.ctaButtonHeightFixRef.nativeElement) {
              const rect = element.getBoundingClientRect();
              try {
                const topPaddingToExclude = 16;
                const newHeight = rect.height > topPaddingToExclude ? rect.height - topPaddingToExclude : rect.height;
                this.renderer.setStyle(this.ctaButtonHeightFixRef.nativeElement, 'height', `${newHeight}px`);
                if (rect.height === 0) {
                  retry$.next(true);
                }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
              } catch (e) {
                // Failed to set height
              }
            }
          }
        })
    );
  }

  private bindBasketHandling(resize$: Observable<Event>) {
    const hideModal$: Observable<boolean> = resize$.pipe(
      map((event) => +event.target?.['innerWidth']),
      filter((width) => width >= 600),
      mapTo(false)
    );

    this.subscription.add(
      hideModal$.subscribe((value) => {
        this.basketOpen$.next(value);
      })
    );
  }

  private cleanUp() {
    this.subscription = unsubscribe(this.subscription);
  }

  reassignFreeSeats(): void {
    this.reassignSeats.emit({ currentFlightId: this.boundsOrFlights[this.slideIndex].id });
  }
}
